import React from 'react';
import { CancelOutlined, FileCopyOutlined, VisibilityOutlined, EditOutlined } from '@material-ui/icons';
import { useHistory } from 'react-router-dom';
import { LinkMenu } from '@components/LinkMenu/LinkMenu';
import { LinkMenuOptionProps } from '@components/LinkMenu/LinkMenuOption/LinkMenuOption';
import {
	useCaseLockStatus
} from '@components/CaseDetails/ManageCaseDropDown/DropDownOptions/EditCase/CaseLockModal/useCaseLockStatus/useCaseLockStatus';
import {
	CANCEL_CASE_MODAL,
	CASE_LOCK_MODAL,
	COPY_CASE_MODAL,
} from '@components/Modal/modalConstants';
import { useModalContext } from '@components/Modal/ModalContextProvider';
import { SurgeonEventProps } from '@components/SchedulePage/SurgeonEvent/SurgeonEventProps';
import { OfficeRoles, OfficeInteractiveRoles } from '@interfaces/UserProfile/UserRoleEnum';
import useGetCaseDetails from '@data/caseDetails/hooks/useGetCaseDetails';
import { AppointmentType } from '@data/request/AppointmentType';
import RequestFormPageState from '@components/RequestForm/RequestFormPage.types';
import useCollection from '@utilities/hooks/useCollection/useCollection';
import { Unit } from '@data/unit/Unit';
import { ENDPOINT_USER_UNITS } from '@utilities/apiConstants';

export const ManageScheduleEventMenu: React.FC<SurgeonEventProps> = ({ eventInfo }) => {
	const history = useHistory<RequestFormPageState>();
	const checkCaseLockStatus = useCaseLockStatus();
	const { openModal } = useModalContext();
	const changeRoute = React.useCallback((path: string) => {
		history.push(path);
	}, [history]);

	const handleCancelCaseClick = React.useCallback(() => {
		checkCaseLockStatus(eventInfo.id, {
			onSuccess: () => openModal(CANCEL_CASE_MODAL.id),
			onFailure: () => openModal(CASE_LOCK_MODAL),
		});
	}, [checkCaseLockStatus, eventInfo.id, openModal]);

	const getCaseDetails = useGetCaseDetails();
	const handleCopyCaseClick = React.useCallback(() => {
		getCaseDetails(eventInfo.id, {
			onSuccess: () => {
				openModal(COPY_CASE_MODAL);
			},
		});
	}, [eventInfo.id, getCaseDetails, openModal]);

	const handleEditCaseClick = React.useCallback(() => {
		checkCaseLockStatus(eventInfo.id, {
			// if case is not locked, get case details in order to populate edit form
			onSuccess: () => getCaseDetails(eventInfo.id, {
				// after details are retrieved, redirect to edit form with isSchedulingEdit set to true
				onSuccess: () => {
					history.push(`/cases/${eventInfo.id}`, {
						isSchedulingEdit: true,
					});
				},
			}),
			// if case is locked, open case locked modal
			onFailure: () => openModal(CASE_LOCK_MODAL),
		});
	}, [checkCaseLockStatus, eventInfo.id, getCaseDetails, openModal, history]);

	const isDocumentRequest = eventInfo.flags.includes(AppointmentType.DOCUMENT);

	const units = useCollection<Unit>(ENDPOINT_USER_UNITS);
	const matchedUnit = React.useMemo(() => units.find(u => u.name === eventInfo.unit || u.id === eventInfo.unit), [units, eventInfo.unit]);
	const unitSchedulingDisabled = Boolean(matchedUnit?.disableScheduling);

	const options: LinkMenuOptionProps[] = React.useMemo(() => {
		return [
			{
				id: 'viewCaseDetails',
				label: 'View Detail',
				icon: VisibilityOutlined,
				onClick: () => changeRoute('/cases/' + eventInfo.id),
				order: 4,
				showForRoles: OfficeRoles,
			},
			{
				id: 'cancelCase',
				label: 'Cancel',
				icon: CancelOutlined,
				onClick: handleCancelCaseClick,
				showForRoles: OfficeInteractiveRoles,
				order: 1
			},
			{
				id: 'copyCase',
				label: 'Copy',
				icon: FileCopyOutlined,
				onClick: handleCopyCaseClick,
				showForRoles: OfficeInteractiveRoles,
				order: 2,
				shouldHide: isDocumentRequest || unitSchedulingDisabled,
			},
			{
				id: 'editCase',
				label: 'Edit',
				icon: EditOutlined,
				onClick: handleEditCaseClick,
				showForRoles: OfficeInteractiveRoles,
				order: 3,
				shouldHide: unitSchedulingDisabled,
			},
		];
	}, [changeRoute, eventInfo.id, handleCancelCaseClick, handleCopyCaseClick, handleEditCaseClick, isDocumentRequest, unitSchedulingDisabled]);

	if (!eventInfo.isPracticeEvent) {
		return null;
	}

	return (
		<LinkMenu
			label="Manage Case"
			options={options}
			data-field="scheduling-event-manageCaseMenu"
		/>
	);
};
